import { useEffect, useState } from 'react';

import { useDocumentZipEvents } from '@/api/hooks/useDocumentZipEvents';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface Props {
  caseId: string;
  caseName: string;
  onStartZipCreation: () => void;
  onDownloadZip: () => void;
  onCheckExisting: () => void;
  isCheckingExisting: boolean;
  zipExists: boolean;
  zipInProgress: boolean;
  zipError?: string;
  hasActiveFilters?: boolean;
  totalDocuments?: number;
}

export const DownloadZipModal = ({
  caseId,
  caseName,
  onStartZipCreation,
  onDownloadZip,
  onCheckExisting,
  isCheckingExisting,
  zipExists,
  zipInProgress,
  zipError,
  hasActiveFilters,
  totalDocuments,
}: Props) => {
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [zipReady, setZipReady] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  useEffect(() => {
    onCheckExisting();
  }, [onCheckExisting]);

  useEffect(() => {
    if (zipInProgress) {
      setIsCreatingZip(true);
    }
  }, [zipInProgress]);

  const handleZipEvent = (event: any) => {
    if (event.type === 'document-zip-progress') {
      setIsCreatingZip(true);
      setZipProgress(event.progress || 0);
    } else if (event.type === 'document-zip-complete') {
      setIsCreatingZip(false);
      setZipProgress(100);
      setZipReady(true);
    } else if (event.type === 'document-zip-error') {
      setIsCreatingZip(false);
    }
  };

  useDocumentZipEvents({
    caseId,
    onEvent: handleZipEvent,
    enabled: !!caseId,
  });

  if (zipError) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-4 px-8 py-6">
        <div className="text-xl font-medium text-red-600">Error creating zip file</div>
        <div className="text-center text-gray-600">{zipError}</div>
        <Button onClick={onStartZipCreation} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  if (isCheckingExisting) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-8">
        <div className="text-xl font-medium text-gray-700">Checking for existing zip...</div>
        <Icon className="lucide--loader-circle fa-spin text-brandSecondary text-4xl" />
      </div>
    );
  }

  if (zipExists || zipReady) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-6 px-8 py-6">
        <Icon className="lucide--check-circle text-5xl text-green-500" />
        <div className="text-xl font-medium text-gray-700">Zip file ready for download</div>
        <div className="text-center text-gray-600">
          {hasActiveFilters
            ? `Filtered documents for case "${caseName}" have been packaged as a zip.`
            : `All documents for case "${caseName}" have been packaged as a zip.`}
          {totalDocuments && hasActiveFilters && (
            <div className="mt-1 text-sm text-gray-500">
              {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} matching filters
            </div>
          )}
        </div>

        <Button onClick={onDownloadZip} className="w-full items-center justify-center">
          {`Download ${caseName}${hasActiveFilters ? ' (filtered)' : ''}.zip`}
          <Icon className="lucide--download h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (isCreatingZip) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-6 px-8 py-6">
        <Icon className="lucide--file-archive text-brandSecondary text-5xl" />
        <div className="text-xl font-medium text-gray-700">
          Creating {hasActiveFilters ? 'filtered ' : ''}zip file...
        </div>
        <div className="w-full max-w-md">
          <div className="mb-2 flex justify-between text-sm text-gray-600">
            <span>Progress</span>
            <span>{Math.round(zipProgress)}%</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-200">
            <div
              className="bg-brandSecondary h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${zipProgress}%` }}
            ></div>
          </div>
        </div>
        <div className="text-center text-sm text-gray-600">
          This may take a few minutes depending on the number and size of documents.
          {hasActiveFilters && totalDocuments && (
            <div className="mt-1">
              Processing {totalDocuments} filtered document{totalDocuments !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-72 flex-col items-center justify-center gap-6 px-8 py-6">
      <Icon className="lucide--loader-circle fa-spin text-brandSecondary text-5xl" />
      <div className="text-xl font-medium text-gray-700">
        Preparing {hasActiveFilters ? 'filtered ' : ''}zip file...
      </div>
      <div className="text-center text-sm text-gray-600">
        Setting up document packaging for "{caseName}".
        {hasActiveFilters && (
          <div className="mt-1 text-gray-500">Filters will be applied to include only matching documents</div>
        )}
      </div>
    </div>
  );
};
